/**
 * Wortweiser Diff (LCS) ohne externe Abhängigkeit — für die Vorher/Nachher-
 * Ansicht der Bericht-Überarbeitung.
 */

export type DiffPart = { type: 'eq' | 'ins' | 'del'; text: string };

/** Zerlegt Text in Wort- und Whitespace-Token (Reihenfolge bleibt erhalten). */
function tokenize(text: string): string[] {
  // split mit Capture-Group behält die Trenner (Whitespace) als eigene Token.
  return (text || '').split(/(\s+)/).filter((t) => t.length > 0);
}

/**
 * Liefert die Diff-Segmente zwischen `before` und `after` auf Wort-Ebene.
 * Bei sehr großen Texten (Schutz vor O(n·m)-Explosion) wird ohne Hervorhebung
 * zurückgegeben (alles als ein del + ein ins).
 */
export function wordDiff(before: string, after: string): DiffPart[] {
  const a = tokenize(before);
  const b = tokenize(after);
  const n = a.length;
  const m = b.length;

  if (n === 0 && m === 0) return [];
  if (n * m > 4_000_000) {
    const parts: DiffPart[] = [];
    if (before) parts.push({ type: 'del', text: before });
    if (after) parts.push({ type: 'ins', text: after });
    return parts;
  }

  // LCS-Längen-Tabelle (von hinten aufgebaut).
  const dp: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const parts: DiffPart[] = [];
  const push = (type: DiffPart['type'], text: string) => {
    const last = parts[parts.length - 1];
    if (last && last.type === type) last.text += text;
    else parts.push({ type, text });
  };

  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { push('eq', a[i]); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { push('del', a[i]); i++; }
    else { push('ins', b[j]); j++; }
  }
  while (i < n) { push('del', a[i]); i++; }
  while (j < m) { push('ins', b[j]); j++; }
  return parts;
}

/** HTML → gut lesbarer Klartext (Block-Struktur als Zeilenumbrüche, Listen als •). */
export function htmlToReadableText(html: string): string {
  if (!html) return '';
  return html
    .replace(/<\s*li[^>]*>/gi, '• ')
    .replace(/<\s*(br|\/p|\/h[1-6]|\/li|\/tr|\/div)\s*\/?\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .split('\n')
    .map((l) => l.replace(/[ \t]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
